import { ReactElement, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { duplicateTimetable } from 'src/api/duplicateTimetable';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useTimetables } from 'src/hooks/useTimetables';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { ICloseDuplicateTrayOptions, IDuplicateTimetableFormValues } from './types';
import styles from './DuplicateTimetableTrayContent.module.scss';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;
export const DUPLICATE_NAME_ERROR = 'Name must be unique for the brand';

export interface DuplicateTimetableTrayContentProps {
  timetableId: string;
  sourceName?: string;
  onClose: (options?: ICloseDuplicateTrayOptions) => void;
  openId: number;
}

export const DuplicateTimetableTrayContent = ({
  timetableId,
  sourceName,
  onClose,
  openId,
}: DuplicateTimetableTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { trackEvent, captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: timetablesData } = useTimetables(15, undefined, undefined, { staleTime: EXISTING_NAMES_STALE_TIME });
  const existingTimetableNames = useMemo(
    () => timetablesData?.pages.flatMap((page) => page.data.map((timetable) => timetable.name)) ?? [],
    [timetablesData],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<IDuplicateTimetableFormValues>({
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  useEffect(() => {
    reset({ name: '' });
    clearError();
  }, [openId]);

  const onSubmit = async (values: IDuplicateTimetableFormValues) => {
    clearError();

    try {
      await duplicateTimetable(timetableId, { name: values.name.trim() });

      await queryClient.invalidateQueries({ queryKey: [QueryKeys.timetables] });
      trackEvent('timetable_duplicated', { timetableId });
      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Timetable duplicated' });
      onClose({ duplicated: true });
    } catch (error) {
      captureException(error, { tags: { action: 'timetable_duplicate' } });
      const message = error instanceof Error ? error.message : undefined;
      showNotification({
        status: SnackbarStatus.ERROR,
        message: message || 'Failed to duplicate timetable. Please try again.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div>
        {sourceName && (
          <Typography size="medium" weight="regular" className={styles['subtitle']}>
            Timetable to be duplicated - <strong>{sourceName}</strong>
          </Typography>
        )}

        <div className={styles['name-field']}>
          <Input
            type="text"
            label="Timetable Name (internal only)"
            register={register('name', {
              required: 'Please enter a timetable name',
              validate: {
                notEmpty: (value) => value.trim().length > 0 || 'Please enter a timetable name',
                isUnique: (value) => {
                  const trimmedValue = value.trim().toLocaleLowerCase();
                  const isDuplicate = existingTimetableNames.some((name) => name.toLocaleLowerCase() === trimmedValue);
                  return !isDuplicate || DUPLICATE_NAME_ERROR;
                },
              },
            })}
            error={errors?.name?.message}
          />
        </div>
      </div>

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={() => onClose()}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton type="submit" size="full-width" loading={isSubmitting} disabled={isSubmitting || !isValid}>
              Duplicate
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default DuplicateTimetableTrayContent;
